import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Film, Loader2, Lock, Play } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { decryptBytes } from '@/lib/albumCrypto';
import { isVideoEntry, playableMime, type AlbumFileEntry } from '@/lib/album';

/** Fetch raw bytes, falling back to the CORS proxy when the storage server blocks direct access. */
async function fetchBytes(url: string): Promise<Uint8Array<ArrayBuffer>> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return new Uint8Array(await res.arrayBuffer());
  } catch (e) {
    console.warn('[locket] direct fetch failed, retrying via CORS proxy:', url, e);
    const res = await fetch(`https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`);
    if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
    return new Uint8Array(await res.arrayBuffer());
  }
}

/** Fetch + decrypt a file blob into an object URL. */
function useDecryptedUrl(entry: AlbumFileEntry | null, albumKey: Uint8Array, enabled: boolean) {
  return useQuery({
    queryKey: ['blob', entry?.sha256],
    queryFn: async () => {
      try {
        const cipher = await fetchBytes(entry!.url);
        const plain = await decryptBytes(albumKey, entry!.nonce, cipher);
        return URL.createObjectURL(new Blob([plain], { type: playableMime(entry!) }));
      } catch (e) {
        console.error('[locket] failed to load/decrypt file:', entry!.name, e);
        throw e;
      }
    },
    enabled: enabled && Boolean(entry),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
}

interface AlbumGalleryProps {
  files: AlbumFileEntry[];
  albumKey: Uint8Array;
}

export function AlbumGallery({ files, albumKey }: AlbumGalleryProps) {
  const [active, setActive] = useState<AlbumFileEntry | null>(null);

  if (files.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 px-8 text-center">
          <Lock className="size-8 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground max-w-sm mx-auto">
            This album is empty. Encrypted photos and videos will appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {files.map((f) => (
          <MediaTile key={f.sha256 + f.nonce} entry={f} albumKey={albumKey} onOpen={() => setActive(f)} />
        ))}
      </div>
      <MediaLightbox entry={active} albumKey={albumKey} onClose={() => setActive(null)} />
    </>
  );
}

function MediaTile({
  entry,
  albumKey,
  onOpen,
}: {
  entry: AlbumFileEntry;
  albumKey: Uint8Array;
  onOpen: () => void;
}) {
  const isVideo = isVideoEntry(entry);
  const blob = useDecryptedUrl(entry, albumKey, !isVideo);

  // Videos: decrypt the encrypted thumbnail eagerly (if one was uploaded).
  const thumbEntry = useMemo<AlbumFileEntry | null>(
    () =>
      isVideo && entry.thumbUrl && entry.thumbNonce
        ? { ...entry, url: entry.thumbUrl, nonce: entry.thumbNonce, sha256: `${entry.sha256}:thumb`, mime: 'image/jpeg' }
        : null,
    [entry, isVideo],
  );
  const thumb = useDecryptedUrl(thumbEntry, albumKey, Boolean(thumbEntry));

  const videoPlaceholder = (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted text-muted-foreground">
      <Film className="size-8" />
      <span className="px-2 text-xs truncate max-w-full">{entry.name}</span>
    </div>
  );

  return (
    <button
      onClick={onOpen}
      className="group relative aspect-square overflow-hidden rounded-xl border bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={entry.name}
    >
      {isVideo ? (
        thumbEntry ? (
          thumb.isPending ? (
            <Skeleton className="h-full w-full rounded-none" />
          ) : thumb.isError ? (
            videoPlaceholder
          ) : (
            <>
              <img src={thumb.data} alt={entry.name} loading="lazy" className="h-full w-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/30">
                <div className="rounded-full bg-black/60 p-3">
                  <Play className="size-6 text-white" />
                </div>
              </div>
            </>
          )
        ) : (
          videoPlaceholder
        )
      ) : blob.isPending ? (
        <Skeleton className="h-full w-full rounded-none" />
      ) : blob.isError ? (
        <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground px-2">
          Failed to decrypt
        </div>
      ) : (
        <img
          src={blob.data}
          alt={entry.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
        <p className="truncate text-left text-xs text-white">{entry.name}</p>
      </div>
    </button>
  );
}

function MediaLightbox({
  entry,
  albumKey,
  onClose,
}: {
  entry: AlbumFileEntry | null;
  albumKey: Uint8Array;
  onClose: () => void;
}) {
  const blob = useDecryptedUrl(entry, albumKey, Boolean(entry));

  return (
    <Dialog open={Boolean(entry)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl p-2 sm:p-4" aria-describedby={undefined}>
        <DialogTitle className="sr-only">{entry?.name ?? 'Media viewer'}</DialogTitle>
        {entry && (
          <div className="space-y-2">
            {blob.isPending ? (
              <div className="flex h-64 items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="size-5 animate-spin" /> Decrypting {entry.name}…
              </div>
            ) : blob.isError ? (
              <div className="flex h-64 items-center justify-center text-muted-foreground">
                Failed to decrypt this file.
              </div>
            ) : isVideoEntry(entry) ? (
              <video
                src={blob.data}
                controls
                autoPlay
                muted
                playsInline
                className="max-h-[75vh] w-full rounded-lg bg-black"
              />
            ) : (
              <img src={blob.data} alt={entry.name} className="max-h-[75vh] w-full rounded-lg object-contain" />
            )}
            <p className="truncate px-1 text-center text-sm text-muted-foreground">{entry.name}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
